import {
  Directive,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { debounceTime, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { Table } from 'primeng/table';
import { TableDataKey } from '../types/table-data-key';
import { isNil, Nillable, once, ReplaySubjectOnce } from '@web/utils';
import { TableRowsSelectionChangeEvent } from '../events/table-rows-selection-change.event';
import { TableComponent } from '../components/table/table.component';
import { RequiredInputParameterException } from '@web/common';

@Directive({
  selector: '[appTableCurrentPageRowsSelection]',
  standalone: true,
})
export class TableCurrentPageRowsSelectionDirective<
  Item,
  Filter,
  ItemAction,
  CollectionAction,
  Action,
>
  implements OnInit, OnDestroy
{
  private readonly primengTable = inject(Table);
  /* prettier-ignore */
  private readonly table = inject<TableComponent<Item, Filter, ItemAction, CollectionAction, Action>>(TableComponent);

  @Input() public bodyRowSelectionDisabledFn: Nillable<(item: Item) => boolean>;
  @Input() public dataKey!: TableDataKey<Item>;
  @Input() public preserveSelectionOnFilter = false;

  @Output() public readonly selectionChangeEvent = new EventEmitter<
    TableRowsSelectionChangeEvent<Item>
  >();

  public isAllSelected = false;

  private included = new Set<Item[TableDataKey<Item>]>();
  private excluded = new Set<Item[TableDataKey<Item>]>();

  private readonly selectAllSource$ = new ReplaySubjectOnce<void>();
  private readonly deselectAllSource$ = new ReplaySubjectOnce<void>();
  private readonly selectionChangesSource$ = new ReplaySubjectOnce<void>();
  private readonly destroy$ = new Subject<void>();

  public get selectionChanges$(): Observable<void> {
    return this.selectionChangesSource$.asObservable();
  }

  public get selectAll$(): Observable<void> {
    return this.selectAllSource$.asObservable();
  }

  public get deselectAll$(): Observable<void> {
    return this.deselectAllSource$.asObservable();
  }

  public ngOnInit(): void {
    this.listenToDeselectAfterRefresh();
    this.listenToDeselectAllAfterFilter();
    this.listenToDeselectAllAfterSort();
    this.listenToDeselectAllAfterPageChange();
    this.listenToSelectionChanges();
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public selectAll(): void {
    this.isAllSelected = true;

    this.clearIncludedAndExcluded();
    this.selectAllSource$.next();
    this.notifySelectionChanges();
  }

  public deselectAll(): void {
    this.isAllSelected = false;

    this.clearIncludedAndExcluded();
    this.deselectAllSource$.next();
    this.notifySelectionChanges();
  }

  public select(item: Item): void {
    const id = this.getIdForItem(item);

    this.included.add(id);
    this.excluded.delete(id);
    this.notifySelectionChanges();
  }

  public deselect(item: Item): void {
    const id = this.getIdForItem(item);

    this.excluded.add(id);
    this.included.delete(id);
    this.notifySelectionChanges();
  }

  public canSelect(item: Item): boolean {
    if (isNil(this.bodyRowSelectionDisabledFn)) {
      throw new RequiredInputParameterException('bodyRowSelectionDisabledFn');
    }

    return !this.bodyRowSelectionDisabledFn(item);
  }

  public isSelected(item: Item): boolean {
    const id = this.getIdForItem(item);

    if (this.included.has(id)) {
      return true;
    }

    if (this.excluded.has(id)) {
      return false;
    }

    if (!this.canSelect(item)) {
      return false;
    }

    return this.isAllSelected;
  }

  public excludedIsEmpty(): boolean {
    return this.excluded.size === 0;
  }

  private listenToDeselectAllAfterSort(): void {
    this.primengTable.onSort
      .pipe(
        switchMap(() => this.table.dataLoaded$.pipe(once())),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => this.deselectAll(),
      });
  }

  private listenToDeselectAllAfterPageChange(): void {
    this.primengTable.onPage
      .pipe(
        switchMap(() => this.table.dataLoaded$.pipe(once())),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => this.deselectAll(),
      });
  }

  private listenToDeselectAfterRefresh(): void {
    this.table.refreshed$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => this.deselectAll(),
    });
  }

  private listenToDeselectAllAfterFilter(): void {
    this.primengTable.onFilter
      .pipe(
        switchMap(() => this.table.dataLoaded$.pipe(once())),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: () => {
          if (this.preserveSelectionOnFilter) {
            return;
          }

          this.deselectAll();
        },
      });
  }

  private listenToSelectionChanges(): void {
    this.selectionChanges$
      .pipe(takeUntil(this.destroy$), debounceTime(200))
      .subscribe({
        next: () => this.notifySelectionChangeEvent(),
      });
  }

  private notifySelectionChanges(): void {
    this.selectionChangesSource$.next();
  }

  private clearIncludedAndExcluded(): void {
    this.included.clear();
    this.excluded.clear();
  }

  private getIdForItem(item: Item): Item[TableDataKey<Item>] {
    return item[this.dataKey];
  }

  private notifySelectionChangeEvent(): void {
    this.selectionChangeEvent.next(
      new TableRowsSelectionChangeEvent(
        this.isAllSelected,
        Array.from(this.included.values()),
        Array.from(this.excluded.values()),
      ),
    );
  }
}
